import {
  Create,
  Edit,
  ArrayInput,
  SimpleFormIterator,
  SimpleForm,
  BooleanInput,
  ReferenceInput,
  required,
  maxLength,
  useRecordContext,
  useResourceContext,
} from "react-admin";
import { parse } from "query-string";
import { useLocation } from "react-router-dom";

import resources from "../../constants/resources";
import { CONTENT_CREATE_TITLE } from "../../constants/titleConstants";
import {
  CONTENT_TYPES_LIST,
  CONTENT_DIFFICULTY,
  SITES,
  KUDZU_SITES,
} from "../../constants/contentConstants";
import { formatImage, getNonNullishValues } from "../../utils/InputUtils";
import CustomToolbar from "../common/CustomToolbar";
import { CONTENT_HELPER_TEXT } from "../../constants/helperTextConstants";
import { PP_ADMIN_REFERRER } from "../../constants/storageKeys";
import {
  AutoCompleteInputField,
  ImageInputField,
  NumberInputField,
  SelectInputField,
  TextInputField,
} from "../../styles/commonStyles";
import PreviewImage from "../common/PreviewImage";

const ContentForm = ({ redirect, topicId, isEdit, order }) => (
  <SimpleForm
    defaultValues={getNonNullishValues({ topic: topicId, order })}
    toolbar={<CustomToolbar redirect={redirect} />}
    sanitizeEmptyValues
  >
    {isEdit && <TextInputField InputProps={{ disabled: true }} source="id" />}
    {/* TODO: KD-169 */}
    <ArrayInput
      source="sites"
      validate={required()}
      helperText={CONTENT_HELPER_TEXT.sites}
    >
      <SimpleFormIterator>
        <SelectInputField
          source="id"
          choices={
            localStorage.getItem(PP_ADMIN_REFERRER) === KUDZU_SITES[0].title
              ? KUDZU_SITES
              : SITES
          }
          validate={required()}
          label="Sites"
        />
      </SimpleFormIterator>
    </ArrayInput>
    <ReferenceInput
      label={resources.TOPIC.label}
      source="topic"
      perPage={800}
      reference={resources.TOPIC.url}
    >
      <AutoCompleteInputField
        optionText="name_en"
        helperText={CONTENT_HELPER_TEXT.topic}
        validate={required()}
      />
    </ReferenceInput>
    <SelectInputField
      source="content_type"
      choices={CONTENT_TYPES_LIST}
      InputProps={{ disabled: isEdit }}
      helperText={
        isEdit
          ? CONTENT_HELPER_TEXT.contentTypeEdit
          : CONTENT_HELPER_TEXT.contentType
      }
      validate={required()}
    />
    <SelectInputField
      source="difficulty"
      choices={CONTENT_DIFFICULTY}
      helperText={CONTENT_HELPER_TEXT.difficulty}
    />
    <NumberInputField
      source="featured_order"
      min="0"
      label="Feature Order"
      helperText={CONTENT_HELPER_TEXT.featureOrder}
    />
    <TextInputField
      source="name_en"
      validate={[required(), maxLength(100)]}
      helperText={CONTENT_HELPER_TEXT.nameEn}
    />
    <TextInputField
      source="name_es"
      validate={maxLength(100)}
      helperText={CONTENT_HELPER_TEXT.nameEs}
    />
    <TextInputField
      multiline
      source="description_en"
      validate={required()}
      helperText={CONTENT_HELPER_TEXT.descriptionEn}
    />
    <TextInputField
      multiline
      source="description_es"
      helperText={CONTENT_HELPER_TEXT.descriptionEs}
    />
    <ImageInputField
      source="cover_image_url"
      format={formatImage}
      label="Cover Image"
      accept="image/*"
      placeholder={<p>Drop your file here</p>}
      validate={required()}
      helperText={CONTENT_HELPER_TEXT.coverImage}
    >
      <PreviewImage source="src" />
    </ImageInputField>
    <TextInputField
      multiline
      source="admin_notes"
      helperText={CONTENT_HELPER_TEXT.adminNotes}
    />
    <NumberInputField
      source="base_point_value"
      min="0"
      validate={required()}
      helperText={CONTENT_HELPER_TEXT.basePointValue}
    />
    <BooleanInput
      defaultValue={false}
      label="Is Published"
      source="is_published"
      InputProps={{ disabled: true }}
      helperText={CONTENT_HELPER_TEXT.isPublished}
    />
  </SimpleForm>
);

const ContentCreate = () => {
  const { search } = useLocation();
  const { topic_id: topicIdString, order: orderString } = parse(search);
  const topicId = topicIdString ? parseInt(topicIdString, 10) : null;
  const order = orderString ? parseInt(orderString, 10) : null;
  const redirect = topicId
    ? `/content-admin/topic/${topicId}/contents`
    : "list";
  return (
    <Create
      title={CONTENT_CREATE_TITLE}
      transform={getNonNullishValues}
      redirect={redirect}
    >
      {ContentForm({ topicId, order, redirect })}
    </Create>
  );
};

const ContentInlineEdit = () => {
  const record = useRecordContext();
  const resource = useResourceContext();
  const redirect = `/content-admin/topic/${record.topic}/contents`;

  return (
    <Edit
      id={record.id}
      resource={resource}
      actions={false}
      mutationMode="pessimistic"
      transform={getNonNullishValues}
      redirect={redirect}
    >
      {ContentForm({ isEdit: true, topicId: record.topic, redirect })}
    </Edit>
  );
};
export { ContentCreate, ContentInlineEdit };
